import { useNodeId, Node } from "@xyflow/react";
import { useCallback } from "react";
import clearOutDataFromNode from "../utils/clearOutDataFromNode";
import { useSetAtom } from "jotai";
import {
  deleteZapStepNodesAndEdgesAtom,
  nodesAtom,
  saveCanvasAsyncCommand,
} from "@zapier/canvas-state";

type Options = { shouldSave: boolean };
type UpdateNodeCallback = (
  partialNode: Partial<Node>,
  options?: Options,
) => void;

const useUpdateNode = (): UpdateNodeCallback => {
  const setNodes = useSetAtom(nodesAtom);
  const deleteZapStepNodesAndEdges = useSetAtom(deleteZapStepNodesAndEdgesAtom);
  const saveToBackend = useSetAtom(saveCanvasAsyncCommand);
  const defaultNodeId = useNodeId();

  /**
   * Updates the properties of the current node.
   *
   * This function accepts a partial node object and merges it
   * with the current node's properties, creating a new node object.
   * This ensures React Flow is notified of the change.
   */
  return useCallback(
    (partialNode, { shouldSave } = { shouldSave: true }) => {
      setNodes((nodes: Node[]) => {
        const nodeId = partialNode.id ?? defaultNodeId;

        const newNodes = nodes.map((node) => {
          if (node.id === nodeId) {
            // it's important that we create a new object here
            // in order to notify react flow about the change
            node = {
              ...node,
              ...partialNode,
              data: {
                ...node.data,
                ...partialNode.data,
              },
            };

            /** This needs to go after the new node is created to clear out values from Zap and default nodes when converting between the two. */
            node = clearOutDataFromNode(node as Node);
          }

          return node;
        });

        if (shouldSave) {
          saveToBackend({ nodes: newNodes });
        }

        return newNodes;
      });

      deleteZapStepNodesAndEdges();
    },
    [defaultNodeId, saveToBackend, setNodes, deleteZapStepNodesAndEdges],
  );
};

export default useUpdateNode;
